import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allVideos, categories } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const categoryInfo = useMemo(() => {
    return categories.find((c) => c.slug === slug);
  }, [slug]);

  const filteredVideos = useMemo(() => {
    if (!slug || slug === 'all') return allVideos;
    return allVideos.filter((v) => v.category === slug);
  }, [slug]);

  const categoryName = categoryInfo?.name || '分类';

  return (
    <>
      <SEOMeta
        title={`${categoryName} - 芭乐视频高清在线观看 | 免费${categoryName}资源`}
        description={categoryInfo?.description || `芭乐视频${categoryName}频道，高清免费在线观看，急速加载不缓冲。海量${categoryName}资源，每日更新。`}
        keywords={`${categoryName},芭乐视频,高清在线观看,免费${categoryName},在线${categoryName}`}
        canonicalUrl={`https://balevideo.com/category/${slug}`}
        ogUrl={`https://balevideo.com/category/${slug}`}
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://balevideo.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName, item: `https://balevideo.com/category/${slug}` },
        ],
      }} />

      <SchemaOrg type="WebPage" data={{
        name: `${categoryName} - 芭乐视频`,
        description: categoryInfo?.description || `芭乐视频${categoryName}频道`,
        url: `https://balevideo.com/category/${slug}`,
        isPartOf: { '@id': 'https://balevideo.com/#website' },
      }} />

      <div className="pt-20">
        <Breadcrumb items={[
          { label: '首页', path: '/' },
          { label: categoryName },
        ]} />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{categoryName}</h1>
            <p className="text-sm text-foreground-600 mt-2">{categoryInfo?.description}</p>
          </div>

          <div className="flex items-center gap-1 px-1 py-1 mb-8 overflow-x-auto flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer font-medium ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {filteredVideos.map((video) => (
              <Link
                key={video.id}
                to={`/category/${video.category}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg aspect-video bg-background-200">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 芭乐视频${categoryName}高清在线观看`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/90 bg-black/40 px-1.5 py-0.5 rounded">{video.duration}</span>
                      <span className="text-xs text-white/90 bg-primary-500/80 px-1.5 py-0.5 rounded">{video.quality}</span>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className="text-xs text-white bg-black/50 px-1.5 py-0.5 rounded">{video.views}次播放</span>
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-play-fill text-white text-xl"></i>
                    </div>
                  </div>
                </div>
                <div className="mt-2.5 px-0.5">
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-500">{video.year}</span>
                    <span className="text-xs text-foreground-400">|</span>
                    <div className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      <span className="text-xs text-foreground-600">{video.rating}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredVideos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-video-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-600">该分类暂无内容</p>
            </div>
          )}
        </section>
      </div>
    </>
  );
}